import { useEffect, useLayoutEffect, useRef, useState } from 'react';

const SLOP = 10;            // px before a drag commits to an axis
const AXIS_RATIO = 1.2;
const TAP_MS = 250;
const DOUBLE_TAP_MS = 300;
const DOUBLE_TAP_SLOP = 30;
const NAV_RATIO = 0.25;     // of stage width
const DISMISS_RATIO = 0.2;  // of stage height
const FLICK_MS = 300;
const FLICK_PX = 60;
const RUBBER = 0.35;
const EASE = 'transform 260ms cubic-bezier(0.22, 0.61, 0.36, 1)';

const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);
const soft = (v, lo, hi) => (v < lo ? lo + (v - lo) * RUBBER : v > hi ? hi + (v - hi) * RUBBER : v);

/**
 * Zoom + pan state machine for a single image.
 *
 * Expects three nested elements: an `overlay` (owns opacity, for the dismiss
 * fade), a `stage` (the pointer target, owns the swipe translate) and the `img`
 * itself (object-fit: contain, owns the zoom transform). contain makes the
 * element's transform-origin coincide with the painted image's centre, which is
 * what keeps the anchored-zoom maths below honest.
 *
 * Scale and offset live in a ref and are written straight to the DOM: React 18
 * schedules pointermove at ContinuousEventPriority, so a setState per move can
 * visibly lag the finger.
 */
export function useZoomPan({
  isMobile = false,
  maxScaleHard = 4,
  tapScale = 2.5,
  onNavigate,
  onDismiss,
  onBackdropTap,
} = {}) {
  const overlayRef = useRef(null);
  const stageRef = useRef(null);
  const imgRef = useRef(null);

  const [ready, setReady] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);

  // Latest callbacks, so the once-registered native listeners never read a stale closure.
  const opt = useRef(null);
  opt.current = { isMobile, maxScaleHard, tapScale, onNavigate, onDismiss, onBackdropTap };

  const st = useRef({
    scale: 1, tx: 0, ty: 0, sx: 0, sy: 0,   // s* = stage swipe offset
    view: { w: 0, h: 0, cx: 0, cy: 0 },     // stage rect, viewport coords
    base: { w: 0, h: 0 },                   // painted image box at scale 1
    maxScale: maxScaleHard,
    pointers: new Map(),
    mode: 'idle',                           // idle|undecided|pan|pinch|swipeX|swipeY|dead
    g: null,
    lastTap: 0,
    lastTapX: 0,
    lastTapY: 0,
    ready: false,
  });

  /* Commit --------------------------------------------------------------- */

  const commit = () => {
    const s = st.current;
    if (imgRef.current) {
      imgRef.current.style.transform = `translate3d(${s.tx}px, ${s.ty}px, 0) scale(${s.scale})`;
    }
    if (stageRef.current) {
      stageRef.current.style.transform = `translate3d(${s.sx}px, ${s.sy}px, 0)`;
    }
    if (overlayRef.current) {
      const p = s.view.h ? Math.min(1, Math.max(0, s.sy) / (0.4 * s.view.h)) : 0;
      overlayRef.current.style.opacity = String(1 - p);
    }
  };

  const animate = (on) => {
    const t = on ? EASE : 'none';
    if (imgRef.current) imgRef.current.style.transition = t;
    if (stageRef.current) stageRef.current.style.transition = t;
  };

  // A permanently promoted 2000px layer costs w*h*4 bytes of raster memory at
  // the scaled size, which is how you get a tab killed on iOS.
  const hint = (on) => {
    if (imgRef.current) imgRef.current.style.willChange = on ? 'transform' : 'auto';
  };

  const syncZoomed = () => {
    const z = st.current.scale > 1.01;
    setIsZoomed((prev) => (prev === z ? prev : z));
  };

  /* Geometry ------------------------------------------------------------- */

  const clampPan = (tx, ty, scale) => {
    const { base, view } = st.current;
    const maxX = Math.max(0, (base.w * scale - view.w) / 2);
    const maxY = Math.max(0, (base.h * scale - view.h) / 2);
    return [clamp(tx, -maxX, maxX), clamp(ty, -maxY, maxY)];
  };

  // Derived analytically from the stage rect and natural aspect ratio - reading
  // back an already-transformed box would drift.
  const measure = () => {
    const stage = stageRef.current;
    const img = imgRef.current;
    if (!stage || !img || !img.naturalWidth) return false;
    const r = stage.getBoundingClientRect();
    if (!r.width || !r.height) return false;

    // The <img> fills the stage but carries padding, so the painted box is the
    // stage rect minus that padding.
    const cs = window.getComputedStyle(img);
    const availW = Math.max(1, r.width - (parseFloat(cs.paddingLeft) || 0) - (parseFloat(cs.paddingRight) || 0));
    const availH = Math.max(1, r.height - (parseFloat(cs.paddingTop) || 0) - (parseFloat(cs.paddingBottom) || 0));
    const ar = img.naturalWidth / img.naturalHeight;
    const w = Math.min(availW, availH * ar);

    const s = st.current;
    s.view = { w: r.width, h: r.height, cx: r.left + r.width / 2, cy: r.top + r.height / 2 };
    s.base = { w, h: w / ar };
    // Never past 1:1 source pixels - no more detail exists, and it caps raster memory.
    s.maxScale = Math.min(opt.current.maxScaleHard, Math.max(1.5, img.naturalWidth / w));
    s.ready = true;
    setReady(true);
    return true;
  };

  /* Zoom ----------------------------------------------------------------- */

  // Keeps the content under (ax, ay) pinned across a scale change. With
  // transform: translate(t) scale(s) about the centre C, a point p maps to
  // C + t + s*(p - C); solving for t' with k = s'/s gives t' = k*t + (1-k)*(a-C).
  const zoomTo = (next, ax, ay, smooth = false) => {
    const s = st.current;
    if (!s.ready) return;
    const k = next / s.scale;
    const tx = k * s.tx + (1 - k) * (ax - s.view.cx);
    const ty = k * s.ty + (1 - k) * (ay - s.view.cy);
    s.scale = next;
    [s.tx, s.ty] = clampPan(tx, ty, next);
    animate(smooth);
    commit();
    syncZoomed();
  };

  // `full` also clears gesture state, for swapping in a different image.
  const reset = (smooth = false, full = false) => {
    const s = st.current;
    Object.assign(s, { scale: 1, tx: 0, ty: 0, sx: 0, sy: 0 });
    if (full) {
      s.pointers.clear();
      s.mode = 'idle';
      s.g = null;
      s.ready = false;
      setReady(false);
    }
    animate(smooth);
    commit();
    syncZoomed();
  };

  const toggleZoom = (x, y) => {
    const s = st.current;
    if (!s.ready) return;
    if (s.scale > 1.01) reset(true);
    else zoomTo(Math.min(opt.current.tapScale, s.maxScale), x, y, true);
  };

  /* Pointers ------------------------------------------------------------- */

  const startPinch = () => {
    const s = st.current;
    const [a, b] = [...s.pointers.values()];
    s.sx = 0;
    s.sy = 0;
    animate(true);
    commit();
    s.mode = 'pinch';
    s.g = {
      s0: s.scale, tx0: s.tx, ty0: s.ty,
      d0: Math.hypot(b.x - a.x, b.y - a.y),
      mx0: (a.x + b.x) / 2, my0: (a.y + b.y) / 2,
    };
  };

  const onPointerDown = (e) => {
    const s = st.current;
    if (!s.ready || e.button > 0 || s.pointers.size >= 2) return;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* gone */ }
    animate(false);
    hint(true);
    s.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY, x0: e.clientX, y0: e.clientY, t0: e.timeStamp });
    if (s.pointers.size === 2) startPinch();
    else {
      s.g = { tx0: s.tx, ty0: s.ty };
      s.mode = s.scale > 1.01 ? 'pan' : 'undecided';
    }
  };

  const onPointerMove = (e) => {
    const s = st.current;
    const p = s.pointers.get(e.pointerId);
    if (!p || !s.ready) return;
    p.x = e.clientX;
    p.y = e.clientY;

    if (s.mode === 'pinch') {
      const [a, b] = [...s.pointers.values()];
      const g = s.g;
      if (!a || !b || !g || g.d0 < 20) return;   // degenerate: fingers too close
      const d = Math.hypot(b.x - a.x, b.y - a.y);
      const mx = (a.x + b.x) / 2;
      const my = (a.y + b.y) / 2;
      const next = soft(g.s0 * (d / g.d0), 1, s.maxScale);
      const k = next / g.s0;
      // Anchored at the midpoint captured at gesture start, plus the midpoint
      // delta, so two fingers also pan.
      s.scale = next;
      [s.tx, s.ty] = clampPan(
        k * g.tx0 + (1 - k) * (g.mx0 - s.view.cx) + (mx - g.mx0),
        k * g.ty0 + (1 - k) * (g.my0 - s.view.cy) + (my - g.my0),
        next
      );
      commit();
      syncZoomed();
      return;
    }

    const dx = p.x - p.x0;
    const dy = p.y - p.y0;

    if (s.mode === 'undecided') {
      if (Math.max(Math.abs(dx), Math.abs(dy)) < SLOP) return;
      // Final for the life of this pointer; never re-evaluated.
      if (Math.abs(dx) > Math.abs(dy) * AXIS_RATIO) s.mode = 'swipeX';
      else if (dy > 0 && dy > Math.abs(dx) * AXIS_RATIO) s.mode = 'swipeY';
      else s.mode = 'dead';                      // upward drag
    }

    if (s.mode === 'pan') [s.tx, s.ty] = clampPan(s.g.tx0 + dx, s.g.ty0 + dy, s.scale);
    else if (s.mode === 'swipeX') { s.sx = dx; s.sy = 0; }
    else if (s.mode === 'swipeY') { s.sx = 0; s.sy = Math.max(0, dy); }
    else return;
    commit();
  };

  const handleTap = (p, now) => {
    const s = st.current;
    const o = opt.current;
    if (Math.hypot(p.x - p.x0, p.y - p.y0) > SLOP || now - p.t0 > TAP_MS) return;

    // A tap on the empty area around the image asks to close, not to zoom in.
    if (s.scale <= 1.01 && o.onBackdropTap) {
      const outside =
        Math.abs(p.x - (s.view.cx + s.tx)) > (s.base.w * s.scale) / 2 ||
        Math.abs(p.y - (s.view.cy + s.ty)) > (s.base.h * s.scale) / 2;
      if (outside) { o.onBackdropTap(); return; }
    }

    const isDouble =
      now - s.lastTap < DOUBLE_TAP_MS &&
      Math.hypot(p.x - s.lastTapX, p.y - s.lastTapY) < DOUBLE_TAP_SLOP;
    s.lastTap = isDouble ? 0 : now;
    s.lastTapX = p.x;
    s.lastTapY = p.y;
    // Desktop toggles on the first click; binding dblclick would cost 300ms
    // every time. Mobile waits for the second tap, so there is no delay to hide.
    if (isDouble || !o.isMobile) toggleZoom(p.x, p.y);
  };

  // pointerup, pointercancel and lostpointercapture all land here: Safari drops
  // capture implicitly, and handling only pointerup leaves the machine stuck.
  const release = (e) => {
    const s = st.current;
    const o = opt.current;
    const p = s.pointers.get(e.pointerId);
    if (!p) return;
    s.pointers.delete(e.pointerId);

    if (s.mode === 'pinch') {
      if (s.pointers.size === 1) {
        // Re-seat the survivor, or the image jumps by the accumulated pinch delta.
        const q = [...s.pointers.values()][0];
        q.x0 = q.x;
        q.y0 = q.y;
        s.g = { tx0: s.tx, ty0: s.ty };
        s.mode = s.scale > 1.01 ? 'pan' : 'undecided';
        return;
      }
      hint(false);
      s.mode = 'idle';
      if (s.scale <= 1.01) reset(true);
      else {
        s.scale = clamp(s.scale, 1, s.maxScale);
        [s.tx, s.ty] = clampPan(s.tx, s.ty, s.scale);
        animate(true);
        commit();
        syncZoomed();
      }
      return;
    }

    if (s.pointers.size > 0) return;

    const { mode } = s;
    const dx = p.x - p.x0;
    const dy = p.y - p.y0;
    const quick = e.timeStamp - p.t0 < FLICK_MS;
    s.mode = 'idle';
    hint(false);

    if (mode === 'swipeX') {
      const go = Math.abs(dx) > s.view.w * NAV_RATIO || (quick && Math.abs(dx) > FLICK_PX);
      s.sx = 0;
      animate(true);
      commit();
      if (go && o.onNavigate) o.onNavigate(dx > 0 ? -1 : 1);
      return;
    }

    if (mode === 'swipeY') {
      if ((dy > s.view.h * DISMISS_RATIO || (quick && dy > FLICK_PX)) && o.onDismiss) {
        o.onDismiss();
        return;
      }
      s.sy = 0;
      animate(true);
      commit();
      return;
    }

    if (mode === 'pan') {
      animate(true);
      [s.tx, s.ty] = clampPan(s.tx, s.ty, s.scale);
      commit();
    }
    // 'pan' falls through too: a zoomed pointer commits to pan on contact, so a
    // click that never moved still has to reach the tap handler.
    if (mode === 'pan' || mode === 'undecided') handleTap(p, e.timeStamp);
  };

  /* Native listeners ----------------------------------------------------- */

  // React 18 registers wheel passively, so preventDefault() in onWheel is a
  // no-op - Ctrl+wheel would zoom the page and plain wheel scroll it behind us.
  const wheel = useRef(null);
  wheel.current = (e) => {
    const s = st.current;
    if (!s.ready) return;
    e.preventDefault();
    const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? s.view.h : 1;
    const speed = e.ctrlKey ? 0.01 : 0.0015;    // ctrlKey == trackpad pinch
    zoomTo(clamp(s.scale * Math.exp(-e.deltaY * unit * speed), 1, s.maxScale), e.clientX, e.clientY);
  };

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    const onWheel = (ev) => wheel.current(ev);
    const block = (ev) => ev.preventDefault();   // Safari-only page pinch-zoom
    el.addEventListener('wheel', onWheel, { passive: false });
    ['gesturestart', 'gesturechange', 'gestureend'].forEach((t) =>
      document.addEventListener(t, block, { passive: false }));
    return () => {
      el.removeEventListener('wheel', onWheel);
      ['gesturestart', 'gesturechange', 'gestureend'].forEach((t) =>
        document.removeEventListener(t, block));
    };
  }, []);

  // A cached image can finish loading before React attaches onLoad.
  useLayoutEffect(() => {
    const img = imgRef.current;
    if (img && img.complete && img.naturalWidth > 0 && !st.current.ready) measure();
  });

  useEffect(() => {
    const el = stageRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    // Also catches the iOS toolbar collapse and orientation change.
    const ro = new ResizeObserver(() => {
      const s = st.current;
      if (!s.ready) return;
      measure();
      s.scale = clamp(s.scale, 1, s.maxScale);
      [s.tx, s.ty] = clampPan(s.tx, s.ty, s.scale);
      animate(false);
      commit();
      syncZoomed();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return {
    ready,
    isZoomed,
    overlayRef,
    reset,
    toggleZoom,
    stageProps: {
      ref: stageRef,
      onPointerDown,
      onPointerMove,
      onPointerUp: release,
      onPointerCancel: release,
      onLostPointerCapture: release,
    },
    imgProps: {
      ref: imgRef,
      draggable: false,
      onDragStart: (e) => e.preventDefault(),
      onLoad: measure,
    },
  };
}

export default useZoomPan;
